import Box, { type BoxProps } from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton, { type SkeletonProps } from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";

type LoadingProps<V extends "skeleton" | "circular" = "circular"> =
  V extends "skeleton"
    ? {
        ref?: Ref<HTMLDivElement>;
        variant: V;
        skeletonProps?: SkeletonProps;
      } & BoxProps
    : {
        textDisabled?: boolean;
        ref?: Ref<HTMLDivElement>;
        variant?: V;
      } & BoxProps;

export function Loading<V extends "skeleton" | "circular" = "circular">(
  props: LoadingProps<V>
) {
  const { t } = useTranslation();
  if (props.variant === "skeleton") {
    const { ref, skeletonProps, ...rest } = props;
    return (
      <Box data-testid="loading-component" ref={ref} {...rest}>
        <Skeleton variant="rounded" height={200} {...skeletonProps} />
      </Box>
    );
  }
  const { ref, textDisabled, ...rest } = props;
  return (
    <Box
      data-testid="loading-component"
      ref={ref}
      sx={{
        width: "100%",
        height: "100%",
        display: "grid",
        placeItems: "center",
        ...rest.sx,
      }}
      {...rest}
    >
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
      >
        {!textDisabled && (
          <Typography variant="h2" color="primary">
            {t("Next:Core:Loading")}
          </Typography>
        )}
        <CircularProgress color="primary" />
      </Stack>
    </Box>
  );
}
